import { Component, DestroyRef, inject, signal, ViewChild, WritableSignal } from "@angular/core";
import { AbstractControl, FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { BehaviorSubject, Observable, combineLatest, finalize, from, map, of, switchMap } from "rxjs";
import {
  DemoAssetAclEnum,
  DemoAssetAclLookup,
  DemoAssetCarouselModel,
  DemoAssetFormViewModel,
  DemoAssetFormViewModelWithFile,
  DemoAssetMediaModel,
  DemoAssetViewModel,
  DemoFormViewModel,
  MaterialModuleModel,
  MaterialType,
  SuggestedBehavior,
  SuggestedDemo
} from "../core/model/ContentManagement";
import { AdminGridColumnsFactory } from "../shared/admin-grid/admin-grid-columns-factory";
import { DemoAssetService } from "../services/demo-asset.service";
import { DxDataGridComponent, DxListComponent } from "devextreme-angular";
import { ToolbarItem, ToolbarItemFactory } from "../shared/toolbar";
import { SelectionChangedEvent } from "devextreme/ui/list";
import DataSource from "devextreme/data/data_source";
import { CustomItemCreatingEvent } from "devextreme/ui/select_box";
import { MetadataService } from "../services/metadata.service";
import { HttpEvent, HttpEventType } from "@angular/common/http";
import { brdCatchError } from "../shared/message-utils";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { confirm } from "devextreme/ui/dialog";
import { SFAccountGridComponent } from "../shared/sfaccount-grid/sfaccount-grid.component";
import { Slide } from "@board/cloud-library/components/carousel";
import { OptionChangedEvent, RowDraggingReorderEvent } from "devextreme/ui/data_grid";

@Component({
  selector: "brd-asset-editor",
  templateUrl: "./demo-asset-editor.component.html",
  styleUrls: ["./demo-asset-editor.component.less"]
})
export class DemoAssetEditorComponent {
  @ViewChild(DxListComponent) modulesList!: DxListComponent;
  @ViewChild("envVarsGrid") envVarsDataGrid!: DxDataGridComponent;
  @ViewChild("materialsGrid") materialGrid!: DxDataGridComponent;
  @ViewChild(SFAccountGridComponent) userGroupGrid!: SFAccountGridComponent;

  title = "";
  loading = false;
  submitting = false;
  canDownloadZip = false;
  exportOptions = {
    enabled: false,
    fileName: ""
  };

  ramConsumptionValidator = signal({
    isValid: true,
    message: "RAM consumption is mandatory!"
  });
  diskConsumptionValidator = signal({
    isValid: true,
    message: "Disk consumption is mandatory!"
  });

  selectedDemoSize = 0;

  assetId: number = 0;
  isActiveDemo: boolean = false;
  formGroup?: FormGroup;
  suggestedFormgGroup?: FormGroup;
  moduelsListValues: {
    id: string;
    text: string;
    form: FormGroup;
  }[] = [];
  selectedGuid?: string;

  columnsEnvVars = [
    AdminGridColumnsFactory.createTextColumn({ fieldName: "key", caption: "Key" }),
    AdminGridColumnsFactory.createTextColumn({ fieldName: "value", caption: "Value" }),
    AdminGridColumnsFactory.createButtonsColumn({
      buttons: [
        {
          icon: "trash",
          name: "delete"
        }
      ]
    })
  ];

  maxPresetSize = 3 * Math.pow(1024, 3);
  maxImageSize = 60 * Math.pow(1024, 2);

  protected maxSlides = 5;
  protected maxSlideSize = 900 * 1024;
  initSources: Slide[] = [];
  imagePreviewInput?: string;
  hoverPreviewInput?: string;

  toolbarItems: ToolbarItem[] = [
    ToolbarItemFactory.createButton({
      icon: "add",
      text: "Variable",
      onClick: () => {
        this.addVar();
      }
    })
  ];

  modulesDataSource?: DataSource;

  industries$ = this.metadataService.industries$;
  categories$ = this.demoAssetService.assetCategories$;

  private assetsSubject = new BehaviorSubject<DemoAssetViewModel[]>([]);
  protected assets$ = this.assetsSubject.asObservable();

  selectedDemos: number[] = [];

  toolbarItemsMaterials: ToolbarItem[] = [
    ToolbarItemFactory.createButton({
      icon: "add",
      text: "Material",
      onClick: () => {
        this.addRow();
      }
    })
  ];

  columnsMaterials = [
    AdminGridColumnsFactory.createLookupColumn({
      fieldName: "type",
      caption: "Type",
      lookup: {
        dataSource: [
          {
            id: MaterialType.pdf,
            value: "Seismic"
          },
          {
            id: MaterialType.video,
            value: "Academy"
          }
        ],
        displayExpr: "value",
        valueExpr: "id"
      }
    }),
    AdminGridColumnsFactory.createTextColumn({ fieldName: "name", caption: "Material" }, { cellTemplate: "mediaTemplate" }),
    AdminGridColumnsFactory.createTextColumn({ fieldName: "url", caption: "Link" }, { visible: false })
  ];

  addRow(): void {
    this.materialGrid.instance.addRow();
  }

  destroyRef = inject(DestroyRef);

  aclValues = DemoAssetAclLookup;
  selectedAcl = DemoAssetAclEnum.Everyone;
  selectedAclGroups: string[] = [];

  disableAcl = signal(false);
  selectedGroupEnumValue = DemoAssetAclEnum.SelectedGroups;

  totalConsumption = signal({
    ram: "0 MB",
    maxRam: "0 MB",
    disk: "0 MB",
    maxDisk: "0 MB"
  });

  materials = signal<MaterialModuleModel[]>([]);
  materialsChanged = signal(false);

  constructor(
    private activatedRoute: ActivatedRoute,
    private demoAssetService: DemoAssetService,
    private router: Router,
    private metadataService: MetadataService
  ) {
    this.loading = true;

    this.activatedRoute.params
      .pipe(
        takeUntilDestroyed(),
        map(res => {
          return res["id"] ? parseInt(res["id"]) : 0;
        }),
        switchMap(id => {
          this.assetId = id;
          let obs: Observable<DemoAssetFormViewModel> = of({} as DemoAssetFormViewModel);
          let obsMedia: Observable<DemoAssetMediaModel> = of({} as DemoAssetMediaModel);
          let obsCarousel: Observable<DemoAssetCarouselModel> = of({} as DemoAssetCarouselModel);

          if (id !== 0) {
            obs = this.demoAssetService.getAsset(id);
            obsMedia = this.demoAssetService.getAssetMedia(id, true);
            obsCarousel = this.demoAssetService.getAssetCarousel(id, true);
          }

          return combineLatest([obs, obsMedia, obsCarousel, this.metadataService.getFormViewModel()]);
        }),
        map((obs: [DemoAssetFormViewModel, DemoAssetMediaModel, DemoAssetCarouselModel, DemoFormViewModel]) => {
          const [selectedAsset, assetMedia, assetCarousel, formVm] = obs;

          this.metadataService.setFunctinionalities(formVm.functionalities);
          this.metadataService.setUseCases(formVm.useCases);
          this.metadataService.setDomains(formVm.domains);
          this.metadataService.setProcesses(formVm.processes);
          this.metadataService.setGroups(formVm.groups);
          this.metadataService.setIndustries(formVm.industries);
          this.metadataService.setAccounts(formVm.accounts);
          this.demoAssetService.setCategories(formVm.categories);

          this.selectedDemoSize = selectedAsset.demoSize ?? 0;

          this.initSources = assetCarousel.images
            ? assetCarousel.images.map(
                x =>
                  ({
                    fileName: x.imagePath.split("/").pop(),
                    source: x.imageSrc,
                    path: x.imagePath
                  }) as Slide
              )
            : [];

          this.imagePreviewInput = assetMedia?.defaultImage ?? undefined;
          this.hoverPreviewInput = assetMedia?.hoverImage ?? undefined;

          const ramConsumptionFormControl = new FormControl(selectedAsset?.ramConsumption ?? 0, [Validators.required, Validators.min(1)]);
          ramConsumptionFormControl.valueChanges.subscribe(value => {
            this.updateCunsumptionValidation(this.formGroup!.controls.ramConsumption, "RAM consumption", this.ramConsumptionValidator);

            this.totalConsumption.update(old => {
              return {
                ...old,
                ram: this.formatConsumption(
                  this.suggestedFormgGroup!.value.suggestedDemos.filter(
                    (x: SuggestedDemo) => x.behavior === SuggestedBehavior.default
                  ).reduce((acc: number, demo: SuggestedDemo) => acc + demo.suggestedRamConsumption, value)
                )
              };
            });
          });

          const diskConsumptionFormControl = new FormControl(selectedAsset?.diskConsumption ?? 0, [Validators.required, Validators.min(1)]);
          diskConsumptionFormControl.valueChanges.subscribe(value => {
            this.updateCunsumptionValidation(this.formGroup!.controls.diskConsumption, "Disk consumption", this.diskConsumptionValidator);

            this.totalConsumption.update(old => {
              return {
                ...old,
                disk: this.formatConsumption(
                  this.suggestedFormgGroup!.value.suggestedDemos.filter(
                    (x: SuggestedDemo) => x.behavior === SuggestedBehavior.default
                  ).reduce((acc: number, demo: SuggestedDemo) => acc + demo.suggestedDiskConsumption, value)
                )
              };
            });
          });

          const defaultFromControl = new FormControl(!!selectedAsset?.default);
          defaultFromControl.valueChanges.subscribe(value => {
            this.disableAcl.set(value == null ? false : value);
            if (value) {
              this.selectedAclGroups = [];
              this.selectedAcl = DemoAssetAclEnum.Everyone;
            }
          });
          this.disableAcl.set(!!selectedAsset?.default);

          this.title = selectedAsset?.name ?? "New demo";
          this.exportOptions = { enabled: true, fileName: `${this.title}-Authorization` };
          this.formGroup = new FormGroup({
            id: new FormControl(selectedAsset?.id ?? 0),
            name: new FormControl(selectedAsset?.name ?? "", Validators.required),
            description: new FormControl(selectedAsset?.description ?? ""),
            ramConsumption: ramConsumptionFormControl,
            diskConsumption: diskConsumptionFormControl,
            version: new FormControl(selectedAsset?.version ?? ""),
            envVars: new FormControl(selectedAsset?.envVars ?? []),
            demoSource: new FormControl(
              selectedAsset?.demoSourceFileName ? [new File([], selectedAsset.demoSourceFileName)] : null,
              Validators.required
            ),
            agentsConfig: new FormControl(
              selectedAsset?.agentsConfigFileName ? selectedAsset.agentsConfigFileName.split(",").map(x => new File([], x)) : null
            ),
            defaultImage: new FormControl(selectedAsset?.defaultImageFileName ? [new File([], selectedAsset.defaultImageFileName)] : null),
            hoveredImage: new FormControl(selectedAsset?.hoverImageFileName ? [new File([], selectedAsset.hoverImageFileName)] : null),
            slides: new FormControl(this.initSources),
            industries: new FormControl(formVm.industries.filter(x => selectedAsset?.industries?.map(y => y.id).includes(x.id)) ?? []),
            lastUpdate: new FormControl(selectedAsset?.lastUpdate ?? new Date().toISOString()),
            default: defaultFromControl,
            execSyncDataModel: new FormControl(!!selectedAsset?.execSyncDataModel),
            categoryId: new FormControl(selectedAsset?.categoryId ?? null)
          });

          this.materials.set(selectedAsset?.materials ?? []);

          const suggestedDemos = selectedAsset?.suggestedDemos ?? [];
          const suggestedDemosControl = new FormControl<SuggestedDemo[]>(suggestedDemos);
          suggestedDemosControl.valueChanges.subscribe(value => {
            const consumptions = value
              ?.filter(x => x.behavior === SuggestedBehavior.default)
              .reduce(
                (acc, sugg) => ({
                  ram: acc.ram + (sugg.suggestedRamConsumption ?? 0),
                  disk: acc.disk + (sugg.suggestedDiskConsumption ?? 0)
                }),
                {
                  ram: this.formGroup?.value?.ramConsumption ?? 0,
                  disk: this.formGroup?.value?.diskConsumption ?? 0
                }
              );
            this.totalConsumption.update(old => ({
              ...old,
              ram: this.formatConsumption(consumptions?.ram),
              disk: this.formatConsumption(consumptions?.disk)
            }));
          });
          this.suggestedFormgGroup = new FormGroup({
            suggestedDemos: suggestedDemosControl
          });

          const consumptions = suggestedDemos
            .filter(x => x.behavior === SuggestedBehavior.default)
            .reduce(
              (acc, sugg) => ({
                ram: acc.ram + (sugg.suggestedRamConsumption ?? 0),
                disk: acc.disk + (sugg.suggestedDiskConsumption ?? 0)
              }),
              {
                ram: selectedAsset?.ramConsumption ?? 0,
                disk: selectedAsset?.diskConsumption ?? 0
              }
            );
          this.totalConsumption.set({
            ram: this.formatConsumption(consumptions.ram),
            maxRam: this.formatConsumption(formVm.limits.maxRamConsumption),
            disk: this.formatConsumption(consumptions.disk),
            maxDisk: this.formatConsumption(formVm.limits.maxDiskConsumption)
          });

          this.moduelsListValues =
            selectedAsset?.modules?.map(m => {
              const guid = Math.random().toString(36).substring(2);

              const controlName = new FormControl(m?.name ?? "");
              return {
                id: guid,
                text: controlName.value!,
                form: new FormGroup({
                  id: new FormControl(m?.id ?? 0),
                  name: controlName,
                  description: new FormControl(m?.description ?? ""),
                  useCases: new FormControl(m?.useCases ?? []),
                  functionalities: new FormControl(m?.functionalities ?? []),
                  assetId: new FormControl(m?.demoAssetId ?? 0)
                })
              };
            }) ?? [];

          this.modulesDataSource = new DataSource({
            store: this.moduelsListValues,
            paginate: false
          });

          this.isActiveDemo = !!selectedAsset?.id && !selectedAsset.draft;
          this.canDownloadZip = !!selectedAsset?.id || !!selectedAsset?.demoSourceFileName;

          this.selectedAcl = selectedAsset?.aclType ?? DemoAssetAclEnum.Everyone;
          this.selectedAclGroups = selectedAsset?.aclSelectedGroups ?? [];
        }),
        switchMap(() => {
          return this.demoAssetService.getAssets().pipe(
            map(res => {
              this.assetsSubject.next(res.filter(x => x.id !== this.assetId));
            })
          );
        })
      )
      .subscribe(() => {
        this.loading = false;
      });
  }

  addModule(): void {
    const currentModules = [...this.modulesList.items];

    const guid = Math.random().toString(36).substring(2);
    const controlName = new FormControl(`New module ${currentModules.length + 1}`);

    const newModule = {
      id: guid,
      text: controlName.value!,
      form: new FormGroup({
        id: new FormControl(0),
        name: controlName,
        description: new FormControl(""),
        useCases: new FormControl([]),
        functionalities: new FormControl([]),
        assetId: new FormControl(0)
      })
    };
    currentModules.push(newModule);
    this.moduelsListValues.push(newModule);

    this.modulesDataSource = new DataSource({
      store: currentModules,
      paginate: false
    });
    this.modulesList.instance.option("selectedItems", [newModule]);
  }

  saveAsset(saveAsDraft = false): void {
    const demoSize = this.formGroup!.value.demoSource[0]?.size != 0 ? this.formGroup!.value.demoSource[0].size : this.selectedDemoSize;
    if (this.formGroup!.invalid && !saveAsDraft) {
      this.updateCunsumptionValidation(this.formGroup!.controls.ramConsumption, "RAM consumption", this.ramConsumptionValidator);
      this.updateCunsumptionValidation(this.formGroup!.controls.diskConsumption, "Disk consumption", this.diskConsumptionValidator);
      this.formGroup!.markAllAsTouched();
      return;
    } else if (saveAsDraft) {
      const nameEditor = this.formGroup!.get("name");
      if (nameEditor!.invalid) {
        nameEditor!.markAsTouched();
        return;
      }
    } else if (this.formGroup!.value.diskConsumption * Math.pow(1024, 2) < demoSize) {
      this.diskConsumptionValidator.set({
        isValid: false,
        message: "Disk consumption must be greater than source size!"
      });
      return;
    }

    let saveAsset$: Observable<HttpEvent<DemoAssetViewModel> | null>;
    if (saveAsDraft && this.isActiveDemo) {
      saveAsset$ = from(
        confirm(
          "Are you sure you want to save this demo as draft?<br>All relations with tempaltes and other demos will be reset.",
          "Save as draft"
        ).then(dialogResult => {
          if (dialogResult) {
            return this.saveAssetObs(saveAsDraft);
          } else {
            return of(null);
          }
        })
      ).pipe(switchMap(r => r));
    } else saveAsset$ = this.saveAssetObs(saveAsDraft);

    saveAsset$.subscribe();
  }

  onMaterialsReorder(e: RowDraggingReorderEvent): void {
    const data = [...(e.component.option("dataSource") as MaterialModuleModel[])];

    data.splice(e.fromIndex, 1);
    data.splice(e.toIndex, 0, e.itemData);
    e.component.option("dataSource", data);
    e.component.refresh();
  }

  onMaterialChanged(e: OptionChangedEvent): void {
    if (e.name === "dataSource" && !this.materialsChanged()) this.materialsChanged.set(true);
  }

  private saveAssetObs(saveAsDraft: boolean): Observable<HttpEvent<DemoAssetViewModel>> {
    const formData: DemoAssetFormViewModelWithFile = this.formGroup!.value;
    const modules = this.modulesList.items.map((mList, i) => {
      const module = mList.form.value;
      module.order = i;
      return module;
    });
    const suggestedDemos = this.suggestedFormgGroup?.value?.suggestedDemos ?? [];
    this.submitting = true;

    let selectedAclGroups: string[] = [];
    if (this.selectedAcl == DemoAssetAclEnum.SelectedGroups) selectedAclGroups = this.userGroupGrid.grid.instance.getSelectedRowKeys();

    return this.demoAssetService
      .addOrUpdateAsset(
        {
          id: formData.id,
          description: formData.description,
          envVars: formData.envVars,
          name: formData.name,
          modules: modules,
          ramConsumption: formData.ramConsumption ?? 0,
          diskConsumption: formData.diskConsumption ?? 0,
          industries: formData.industries,
          materials: this.materials(),
          version: formData.version,
          suggestedDemos,
          lastUpdate: formData.lastUpdate,
          draft: saveAsDraft,
          default: formData.default,
          execSyncDataModel: formData.execSyncDataModel,
          aclType: this.selectedAcl,
          aclSelectedGroups: selectedAclGroups,
          categoryId: formData.categoryId
        },
        formData.demoSource && formData.demoSource[0].size > 0 ? formData.demoSource[0] : undefined,
        formData.defaultImage && !!formData.defaultImage.length ? formData.defaultImage[0] : undefined,
        formData.hoveredImage && !!formData.hoveredImage.length ? formData.hoveredImage[0] : undefined,
        formData.slides,
        formData.agentsConfig
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        brdCatchError(),
        finalize(() => {
          this.submitting = false;
        }),
        map(res => {
          if (res.type == HttpEventType.Response && res.status >= 200 && res.status < 300) {
            this.router.navigate(["/admin-portal", { selectedTab: 2 }]);
          }
          return res;
        })
      );
  }

  cancel(): void {
    this.router.navigate(["/admin-portal", { selectedTab: 2 }]);
  }

  addVar(): void {
    this.envVarsDataGrid.instance.addRow();
  }

  moduleSelectionChanged(e: SelectionChangedEvent): void {
    this.selectedGuid = e.addedItems[0]?.id;
  }

  customItemIndustry(e: CustomItemCreatingEvent): void {
    if (e.text) {
      const newItem = {
        id: 0,
        name: e.text
      };
      e.customItem = newItem;
    }
  }

  updateCunsumptionValidation(
    control: AbstractControl,
    controlName: string,
    validationSignal: WritableSignal<{ isValid: boolean; message: string }>
  ): void {
    if (control.errors?.required) {
      validationSignal.set({ isValid: false, message: `${controlName} is mandatory!` });
    } else if (control.errors?.min) {
      validationSignal.set({ isValid: false, message: `${controlName} must be greater than 0!` });
    } else {
      validationSignal.set({ isValid: true, message: "" });
    }
  }

  downlaodZip(): void {
    this.submitting = true;
    this.demoAssetService
      .downloadZip(this.assetId)
      .pipe(
        finalize(() => {
          this.submitting = false;
        })
      )
      .subscribe();
  }

  downloadMedia = (path: string): Observable<string> => {
    return this.demoAssetService.getOriginalMedia(path);
  };

  private formatConsumption(value: number): string {
    if (value >= 1000) {
      return `${parseFloat((value / 1000).toFixed(2))} GB`;
    } else {
      return `${value} MB`;
    }
  }
}
